import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, CalendarDays, Copy, Printer, RotateCcw, Users } from "lucide-react";
import { toast } from "sonner";
import { streamSSE } from "@/lib/api";
import { ideasStore, useIdeas } from "@/lib/storage";
import { OverallScore, ScoreBars } from "@/components/Scores";
import { SaveButtons } from "@/components/IdeaCard";
import { IdeaInsights } from "@/components/IdeaInsights";
import { ShareButton } from "@/components/ShareButton";

const Row = ({ label, children }) => (
  <div className="py-3 border-b border-white/5 last:border-0">
    <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className="text-sm text-slate-200 mt-1 leading-relaxed">{children || "—"}</div>
  </div>
);

const AiSection = ({ idea, field, endpoint, resultKey, title, icon: Icon, emptyText, loadingText, testPrefix: tp }) => {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const saved = idea[field];
  const plan = loading ? text : saved;

  const generate = async () => {
    setLoading(true);
    setText("");
    try {
      const { plan, competitors, ...rest } = idea;
      const res = await streamSSE(endpoint, { idea: rest }, (t) => setText((s) => s + t));
      ideasStore.update(idea.id, { [field]: res[resultKey] });
      toast.success(`${title}: pronto`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const copy = () => navigator.clipboard.writeText(saved).then(() => toast.success("Copiato"));

  return (
    <section className="rounded-3xl bg-[#12141F] border border-white/[0.07] p-6 sm:p-8" data-testid={`${tp}-section`}>
      <div className="flex flex-wrap items-center justify-between gap-4 no-print">
        <div className="flex items-center gap-3">
          <Icon className="w-5 h-5 text-amber-400" />
          <h2 className="font-display text-lg md:text-xl font-bold">{title}</h2>
        </div>
        <div className="flex gap-2">
          {saved && !loading && (
            <>
              <button onClick={copy} data-testid={`${tp}-copy-btn`} className="p-2.5 rounded-full border border-white/10 text-slate-300 hover:text-amber-300 transition-colors" title="Copia"><Copy className="w-4 h-4" /></button>
              <button onClick={() => window.print()} data-testid={`${tp}-print-btn`} className="p-2.5 rounded-full border border-white/10 text-slate-300 hover:text-amber-300 transition-colors" title="Stampa / PDF"><Printer className="w-4 h-4" /></button>
            </>
          )}
          <button onClick={generate} disabled={loading} data-testid={`${tp}-generate-btn`}
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors duration-200 disabled:opacity-60 ${saved ? "border border-white/10 text-slate-300 hover:text-amber-300" : "bg-amber-500 text-slate-950 hover:bg-amber-400 forge-pulse"}`}>
            {saved ? <><RotateCcw className="w-4 h-4" /> Rigenera</> : loading ? "Scrittura in corso…" : "Genera"}
          </button>
        </div>
      </div>
      {plan ? (
        <div className={`md-body mt-6 ${loading ? "cursor-blink" : ""}`} data-testid={`${tp}-content`}>
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ a: (p) => <a {...p} target="_blank" rel="noopener noreferrer" className="text-amber-300 underline underline-offset-2" /> }}>{plan}</ReactMarkdown>
        </div>
      ) : (
        <p className="text-slate-400 mt-5 text-sm" data-testid={`${tp}-empty`}>
          {loading ? loadingText : emptyText}
        </p>
      )}
    </section>
  );
};

export default function IdeaDetail() {
  const { id } = useParams();
  const idea = useIdeas().find((i) => i.id === id);

  if (!idea) {
    return (
      <div className="max-w-3xl mx-auto px-4 pt-24 text-center" data-testid="idea-not-found">
        <h1 className="font-display text-3xl font-bold">Idea non trovata</h1>
        <p className="text-slate-400 mt-3">Le idee sono salvate in questo browser. Generane una nuova per iniziare.</p>
        <Link to="/genera" className="inline-block mt-6 rounded-full bg-amber-500 px-6 py-3 font-semibold text-slate-950 hover:bg-amber-400">Vai al generatore</Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 space-y-8" data-testid="idea-detail-page">
      <Link to={-1} className="no-print inline-flex items-center gap-2 text-sm text-slate-400 hover:text-amber-300 transition-colors" data-testid="idea-back-link">
        <ArrowLeft className="w-4 h-4" /> Indietro
      </Link>
      <div className="grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8">
          <div className="overline-label">{idea.categoria} · {idea.source === "verifica" ? "La tua idea" : "Generata dall'AI"}</div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight mt-4 leading-[1.05]" data-testid="idea-detail-name">{idea.nome}</h1>
          <p className="text-lg text-slate-300 mt-5 leading-relaxed">{idea.pitch}</p>
          <p className="text-slate-400 mt-4 leading-relaxed">{idea.descrizione}</p>
          <div className="mt-6 no-print flex flex-wrap items-center gap-3"><SaveButtons idea={idea} prefix="idea-detail" /><ShareButton idea={idea} /></div>
        </div>
        <aside className="lg:col-span-4 rounded-3xl bg-[#12141F] border border-white/[0.07] p-6">
          <div className="flex items-center justify-between">
            <div className="font-display font-semibold">Valutazione AI</div>
            <OverallScore value={idea.punteggio_complessivo} testId="idea-detail-overall" />
          </div>
          <div className="mt-5"><ScoreBars scores={idea.punteggi} testPrefix="idea-detail" /></div>
          <p className="mt-5 text-sm italic text-slate-400 border-l-2 border-amber-500/60 pl-3">{idea.giudizio}</p>
        </aside>
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <div className="rounded-3xl bg-[#12141F] border border-white/[0.07] px-6 py-3">
          <Row label="Problema risolto">{idea.problema}</Row>
          <Row label="Cliente ideale">{idea.cliente_ideale}</Row>
          <Row label="Costi di avvio">{idea.costi_avvio}</Row>
          <Row label="Tempo per il primo guadagno">{idea.tempo_primo_guadagno}</Row>
          <Row label="Potenziale di guadagno">{idea.guadagno_potenziale}</Row>
        </div>
        <div className="rounded-3xl bg-[#12141F] border border-white/[0.07] p-6" data-testid="idea-revenue-models">
          <div className="font-display font-semibold">Come si guadagna</div>
          <div className="mt-4 space-y-3">
            {(idea.modelli_guadagno || []).map((m) => (
              <div key={m.tipo + m.dettaglio} className="rounded-xl bg-emerald-500/[0.06] border border-emerald-500/15 p-4">
                <div className="text-sm font-semibold text-emerald-300">{m.tipo}</div>
                <div className="text-sm text-slate-300 mt-1 leading-relaxed">{m.dettaglio}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <IdeaInsights idea={idea} />
      <AiSection idea={idea} field="competitors" endpoint="/ideas/competitors" resultKey="report" testPrefix="competitors"
        title="Analisi concorrenti" icon={Users}
        loadingText="L'AI sta cercando sul web i concorrenti reali… può richiedere un minuto."
        emptyText="Scopri chi fa già qualcosa di simile, quanto è affollato il mercato e come puoi distinguerti. L'AI cerca sul web in tempo reale." />
      <AiSection idea={idea} field="plan" endpoint="/ideas/plan" resultKey="plan" testPrefix="plan"
        title="Piano d'azione — primi 30 giorni" icon={CalendarDays}
        loadingText="L'AI sta preparando il tuo piano…"
        emptyText="Ottieni azioni concrete settimana per settimana, gli strumenti da usare e come trovare i primi clienti." />
    </div>
  );
}
